/**
 * Word-level diff helpers used to render old-vs-new text changes as <del>/<ins> HTML.
 * diffHtml is used by both the applicability-question review dialog and the rulebook
 * review dialog (edit-template.component.ts DisplayReviewDialog); the latter still keeps
 * its own historical copy of the plain-text diffString.
 */

export function decodeHtml(html: string): string {
  if (html == null) return "";
  var txt = document.createElement("textarea");
  txt.innerHTML = html;
  return txt.value;
}

export function stripHtml(html: string): string {
  return html.replace(/<[^>]*>/g, "");
}

export function processHtml(html: string): string {
  return stripHtml(decodeHtml(html));
}

export function diff(o, n) {
  var ns = new Object();
  var os = new Object();

  for (var i = 0; i < n.length; i++) {
    if (ns[n[i]] == null) ns[n[i]] = { rows: new Array(), o: null };
    ns[n[i]].rows.push(i);
  }

  for (var i = 0; i < o.length; i++) {
    if (os[o[i]] == null) os[o[i]] = { rows: new Array(), n: null };
    os[o[i]].rows.push(i);
  }

  for (var j in ns) {
    if (
      ns[j].rows.length == 1 &&
      typeof os[j] != "undefined" &&
      os[j].rows.length == 1
    ) {
      n[ns[j].rows[0]] = { text: n[ns[j].rows[0]], row: os[j].rows[0] };
      o[os[j].rows[0]] = { text: o[os[j].rows[0]], row: ns[j].rows[0] };
    }
  }

  for (var i = 0; i < n.length - 1; i++) {
    if (
      n[i].text != null &&
      n[i + 1].text == null &&
      n[i].row + 1 < o.length &&
      o[n[i].row + 1].text == null &&
      n[i + 1] == o[n[i].row + 1]
    ) {
      n[i + 1] = { text: n[i + 1], row: n[i].row + 1 };
      o[n[i].row + 1] = { text: o[n[i].row + 1], row: i + 1 };
    }
  }

  for (var i = n.length - 1; i > 0; i--) {
    if (
      n[i].text != null &&
      n[i - 1].text == null &&
      n[i].row > 0 &&
      o[n[i].row - 1].text == null &&
      n[i - 1] == o[n[i].row - 1]
    ) {
      n[i - 1] = { text: n[i - 1], row: n[i].row - 1 };
      o[n[i].row - 1] = { text: o[n[i].row - 1], row: i - 1 };
    }
  }

  return { o: o, n: n };
}

type HtmlToken = { kind: "tag" | "space" | "word"; text: string };

// Split HTML into tags, whitespace runs and words. Tag boundaries also end a word, so
// "</li><li>Note" yields separate words instead of gluing them together the way
// stripping the tags first does.
function tokenizeHtml(html: string): HtmlToken[] {
  const tokens: HtmlToken[] = [];
  const re = /<[^>]*>|\s+|[^<\s]+|</g;
  let m: RegExpExecArray | null;
  while ((m = re.exec(html)) !== null) {
    const text = m[0];
    const kind = /^<[^>]*>$/.test(text) ? "tag" : /^\s+$/.test(text) ? "space" : "word";
    tokens.push({ kind, text });
  }
  return tokens;
}

// Longest-common-subsequence word diff. Returns, for each new word, whether it was inserted,
// and for each position in the new words (0..n.length), the old words deleted just before it.
function diffWords(o: string[], n: string[]) {
  // The common prefix/suffix needs no table; it's usually most of the text.
  let pre = 0;
  while (pre < o.length && pre < n.length && o[pre] === n[pre]) pre++;
  let suf = 0;
  while (
    suf < o.length - pre &&
    suf < n.length - pre &&
    o[o.length - 1 - suf] === n[n.length - 1 - suf]
  ) {
    suf++;
  }
  const oMid = o.slice(pre, o.length - suf);
  const nMid = n.slice(pre, n.length - suf);

  const cols = nMid.length + 1;
  const lcs = new Int32Array((oMid.length + 1) * cols);
  for (let i = oMid.length - 1; i >= 0; i--) {
    for (let j = nMid.length - 1; j >= 0; j--) {
      lcs[i * cols + j] =
        oMid[i] === nMid[j]
          ? lcs[(i + 1) * cols + j + 1] + 1
          : Math.max(lcs[(i + 1) * cols + j], lcs[i * cols + j + 1]);
    }
  }

  const inserted = new Array<boolean>(n.length).fill(false);
  const deletedBefore: string[][] = Array.from({ length: n.length + 1 }, () => []);
  let i = 0;
  let j = 0;
  while (i < oMid.length || j < nMid.length) {
    if (i < oMid.length && j < nMid.length && oMid[i] === nMid[j]) {
      i++;
      j++;
    } else if (
      i < oMid.length &&
      (j >= nMid.length || lcs[(i + 1) * cols + j] >= lcs[i * cols + j + 1])
    ) {
      // Deletions first, so a replaced phrase reads old (struck) then new.
      deletedBefore[pre + j].push(oMid[i]);
      i++;
    } else {
      inserted[pre + j] = true;
      j++;
    }
  }
  return { inserted, deletedBefore };
}

/**
 * Like diffString, but keeps the new version's formatting: the words of the two HTML
 * fragments are compared (tags are ignored, so a formatting-only change isn't marked), and
 * the result is the new HTML with inserted words wrapped in <ins> and deleted words put back
 * as <del> where they were. Expects decoded HTML (see decodeHtml).
 */
export function diffHtml(oldHtml: string, newHtml: string): string {
  const normalize = (html: string) => (html ?? "").replace(/&nbsp;/gi, " ");
  const oWords = tokenizeHtml(normalize(oldHtml))
    .filter((t) => t.kind === "word")
    .map((t) => t.text);
  const nTokens = tokenizeHtml(normalize(newHtml));
  const nWords = nTokens.filter((t) => t.kind === "word").map((t) => t.text);
  const { inserted, deletedBefore } = diffWords(oWords, nWords);

  const del = (words: string[]) => `<del>${words.join(" ")}</del>`;
  let lastWordToken = -1;
  nTokens.forEach((t, k) => {
    if (t.kind === "word") lastWordToken = k;
  });

  let out = "";
  let inIns = false;
  const closeIns = () => {
    if (inIns) {
      out += "</ins>";
      inIns = false;
    }
  };

  let w = 0;
  nTokens.forEach((t, k) => {
    if (t.kind === "word") {
      if (deletedBefore[w].length > 0) {
        closeIns();
        out += del(deletedBefore[w]) + " ";
      }
      if (inserted[w]) {
        if (!inIns) {
          out += "<ins>";
          inIns = true;
        }
      } else {
        closeIns();
      }
      out += t.text;
      w++;
      if (k === lastWordToken) {
        // Old words deleted after the last new word stay inside its element.
        closeIns();
        if (deletedBefore[nWords.length].length > 0) {
          out += " " + del(deletedBefore[nWords.length]);
        }
      }
    } else if (t.kind === "space") {
      out += t.text; // kept inside an open <ins>, so a run of new words is one highlight
    } else {
      closeIns(); // never let <ins> straddle a tag
      out += t.text;
    }
  });
  closeIns();

  if (lastWordToken === -1 && deletedBefore[0].length > 0) {
    out += del(deletedBefore[0]); // the new version has no words at all
  }
  return out;
}

export function diffString(o: string, n: string): string {
  o = o.replace(/\s+$/, "");
  n = n.replace(/\s+$/, "");

  var out = diff(o == "" ? [] : o.split(/\s+/), n == "" ? [] : n.split(/\s+/));
  var str = "";

  var oSpace = o.match(/\s+/g);
  if (oSpace == null) {
    oSpace = ["\n"];
  } else {
    oSpace.push("\n");
  }
  var nSpace = n.match(/\s+/g);
  if (nSpace == null) {
    nSpace = ["\n"];
  } else {
    nSpace.push("\n");
  }

  if (out.n.length == 0) {
    for (var i = 0; i < out.o.length; i++) {
      str += "<del>" + out.o[i] + oSpace[i] + "</del>";
    }
  } else {
    if (out.n[0].text == null) {
      for (n = 0 as any; (n as any) < out.o.length && out.o[n as any].text == null; (n as any)++) {
        str += "<del>" + out.o[n as any] + oSpace[n as any] + "</del>";
      }
    }

    for (var i = 0; i < out.n.length; i++) {
      if (out.n[i].text == null) {
        str += "<ins>" + out.n[i] + nSpace[i] + "</ins>";
      } else {
        var pre = "";

        for (
          n = (out.n[i].row + 1) as any;
          (n as any) < out.o.length && out.o[n as any].text == null;
          (n as any)++
        ) {
          pre += "<del>" + out.o[n as any] + oSpace[n as any] + "</del>";
        }
        str += " " + out.n[i].text + nSpace[i] + pre;
      }
    }
  }

  return str;
}
